import React from "react";
import { Doughnut } from "react-chartjs-2";
import Chart from "chart.js";
import { useTranslation } from "react-i18next";


Chart.pluginService.register({
    beforeDraw: function (chart) {
        const opts = chart?.config?.options;
        const center = opts?.elements?.center;
        if (!center) return;

        const ctx = chart.chart.ctx;
        const area = chart.chartArea;
        const cx = (area.left + area.right) / 2;
        const cy = (area.top + area.bottom) / 2;

        const fontFamily = center.fontFamily ||
            'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';

        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        if (center.topText) {
            ctx.font = `600 ${center.topFontSize || 14}px ${fontFamily}`;
            ctx.fillStyle = center.topColor || "#424D64";
            ctx.fillText(center.topText, cx, cy - (center.topOffset || 20));
        }

        if (center.middleText) {
            ctx.font = `400 ${center.middleFontSize || 22}px ${fontFamily}`;
            ctx.fillStyle = center.middleColor || "#424D64";
            ctx.fillText(center.middleText, cx, cy + (center.middleOffset || 0));
        }

        if (center.bottomText) {
            ctx.font = `400 ${center.bottomFontSize || 12}px ${fontFamily}`;
            ctx.fillStyle = center.bottomColor || "#424D64";
            ctx.fillText(center.bottomText, cx, cy + (center.bottomOffset || 22));
        }

        ctx.restore();
    },
});

type AgrDonutProps = {
    label?: string,
    subtitle?: string,
    nextToExp?: number,
    expired?: number,
    count?: number
}

const AgrDonutChart: React.FC<AgrDonutProps> = ({ label, subtitle, nextToExp, expired, count }) => {
    const { t } = useTranslation();
    const colors = { small: "#416DBC", rest: "#00CFDD" }
    const rotationOffset = -0.65

    const zeroData = (nextToExp ?? 0) === 0 && (expired ?? 0) === 0;
    const chartData = zeroData ? [1] : [nextToExp ?? 0, expired ?? 0];
    const chartColors = zeroData ? ["#D0D5E0"] : [colors.small, colors.rest];

    const data = {
        labels: [],
        datasets: [
            {
                data: chartData,
                backgroundColor: chartColors,
                borderWidth: 0,
                hoverBorderWidth: 0,
            },
        ],
    };

    const options = {
        cutoutPercentage: 86,
        rotation: -Math.PI / 2 + rotationOffset,
        circumference: 2 * Math.PI,
        legend: { display: false },
        tooltips: { enabled: false },
        animation: { animateRotate: true, animateScale: false },
        maintainAspectRatio: false,
        elements: {
            center: {
                topText: label,
                topFontSize: 13,
                topOffset: 24,
                middleText: String(count),
                middleFontSize: 22,
                middleOffset: -2,
                bottomText: subtitle,
                bottomFontSize: 12,
                bottomOffset: 24,
            },
        },
        plugins: {
            datalabels: false
        }
    };

    return (
        <div className="agr-chart-wrap">
            <div className="agr-chart-canvas">
                <Doughnut data={data} options={options} />
            </div>

            {((nextToExp ?? 0) + (expired ?? 0) > 0) ? (
                <div className="agr-donut">
                    <div className="agr-content-donut">
                        <div className="flex-dot-agr">
                            <div className="data-donut-agr-dot"></div>
                            <div>
                                <p className="data-donut-agr">{expired}</p>
                                <p className="label-donut-agr">{t('extended_report.expired_projects')}</p>
                            </div>
                        </div>
                    </div>

                    <div className="agr-content-donut">
                        <div className="flex-dot-agr">
                            <div className="data-donut-agr-second-dot"></div>
                            <div>
                                <p className="data-donut-agr">{nextToExp}</p>
                                <p className="label-donut-agr">{t('extended_report.close_to_expiring')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="agr-text-no-agreements">
                    <p>{t('extended_report.zero_agreements')}</p>
                </div>
            )}
        </div>
    );
}

export default AgrDonutChart